import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { CreateEmployeeForm, ModulePerm, EMPTY_PERM, anyPerm } from './shared';

interface Props {
  form: CreateEmployeeForm;
  setForm: React.Dispatch<React.SetStateAction<CreateEmployeeForm>>;
  editingId: number | null;
  grantableModules: any[];
  saving: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}

/**
 * Full-page create/edit employee form (Details + Pay + Module access).
 * Extracted verbatim from the old AttendanceApprovals page so behaviour is unchanged.
 */
const EmployeeFormPanel: React.FC<Props> = ({ form, setForm, editingId, grantableModules, saving, onCancel, onSubmit }) => {
  // Flip one capability for a module; drop the module entirely once nothing is granted.
  const togglePerm = (moduleId: number, key: keyof ModulePerm) =>
    setForm((f) => {
      const updated = { ...(f.modulePerms[moduleId] || EMPTY_PERM), [key]: !(f.modulePerms[moduleId]?.[key]) };
      const next = { ...f.modulePerms };
      if (anyPerm(updated)) next[moduleId] = updated; else delete next[moduleId];
      return { ...f, modulePerms: next };
    });

  // Quick-set every grantable module to the same capability set (or clear all when null).
  const setAllPerms = (perm: ModulePerm | null) =>
    setForm((f) => ({
      ...f,
      modulePerms: perm
        ? grantableModules.reduce((a, m) => { a[m.id] = { ...perm }; return a; }, {} as Record<number, ModulePerm>)
        : {},
    }));

  return (
    <div className="p-4 sm:p-6 w-full">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={onCancel}
            className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition-colors"
            title="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{editingId ? 'Edit Employee' : 'Add Employee'}</h1>
            <p className="text-gray-500 text-sm">
              {editingId ? 'Update the details and module access for this employee.' : 'Create a login and choose exactly what this employee can access.'}
            </p>
          </div>
        </div>

        <div className="space-y-5">
          {/* Details */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
            <h2 className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-4">Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Name <span className="text-red-500">*</span></label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-shadow" placeholder="Employee name" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Designation</label>
                <input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-shadow" placeholder="e.g. Karigar, Master" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Email <span className="text-red-500">*</span></label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-shadow" placeholder="login email" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Phone</label>
                <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-shadow" placeholder="phone (can be used to log in)" />
              </div>
              {!editingId && (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Password <span className="text-red-500">*</span></label>
                  <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-shadow" placeholder="min 6 characters" />
                </div>
              )}
            </div>

            {/* Pay */}
            <div className="mt-5 pt-5 border-t border-gray-100">
              <h3 className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-3">Pay</h3>
              <div className="flex gap-2 mb-4">
                {(['FIXED', 'METER'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, payType: t }))}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${form.payType === t ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                  >
                    {t === 'FIXED' ? 'Fixed monthly' : 'Per meter'}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {form.payType === 'FIXED' ? (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Monthly Salary (₹)</label>
                    <input type="number" min="0" step="1" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-shadow" placeholder="e.g. 15000" />
                    <p className="text-[11px] text-gray-400 mt-1">Flat monthly base pay.</p>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Shift A (₹/meter)</label>
                      <input type="number" min="0" step="0.01" value={form.shiftARate} onChange={(e) => setForm({ ...form, shiftARate: e.target.value })} className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-shadow" placeholder="e.g. 2.5" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Shift B (₹/meter)</label>
                      <input type="number" min="0" step="0.01" value={form.shiftBRate} onChange={(e) => setForm({ ...form, shiftBRate: e.target.value })} className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-shadow" placeholder="e.g. 3.0" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Default (₹/meter)</label>
                      <input type="number" min="0" step="0.01" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-shadow" placeholder="fallback" />
                      <p className="text-[11px] text-gray-400 mt-1">Used when a shift rate is blank.</p>
                    </div>
                  </>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Extra pay unit</label>
                  <div className="flex gap-2">
                    {(['MACHINE', 'HOUR'] as const).map((u) => (
                      <button
                        key={u}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, extraUnit: u }))}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${form.extraUnit === u ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                      >
                        {u === 'MACHINE' ? 'Per machine' : 'Per hour'}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Extra rate (₹ per {form.extraUnit === 'HOUR' ? 'hour' : 'machine'})</label>
                  <input type="number" min="0" step="1" value={form.extraRate} onChange={(e) => setForm({ ...form, extraRate: e.target.value })} className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-shadow" placeholder="e.g. 100" />
                  <p className="text-[11px] text-gray-400 mt-1">Added per approved extra {form.extraUnit === 'HOUR' ? 'hour' : 'machine'}.</p>
                </div>
              </div>
            </div>
            <label htmlFor="createCanApprove" className="flex items-start gap-3 p-3 mt-4 rounded-lg border border-gray-200 hover:border-gray-300 cursor-pointer transition-colors">
              <input id="createCanApprove" type="checkbox" checked={form.canApproveAttendance} onChange={(e) => setForm((f) => ({ ...f, canApproveAttendance: e.target.checked }))} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              <span className="text-sm text-gray-700">
                <span className="font-semibold">Can approve attendance</span>
                <span className="block text-xs text-gray-500 mt-0.5">Trusted manager — may approve other employees' clock-ins.</span>
              </span>
            </label>
          </div>

          {/* Module Access */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
              <h2 className="text-xs font-bold uppercase tracking-wide text-gray-400">Module Access</h2>
              <div className="flex items-center gap-2 text-xs">
                {Object.keys(form.modulePerms).length > 0 && (
                  <span className="font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">{Object.keys(form.modulePerms).length} granted</span>
                )}
                {grantableModules.length > 0 && (
                  <>
                    <button type="button" onClick={() => setAllPerms({ read: true, create: false, edit: false, delete: false })} className="font-semibold text-blue-600 hover:underline">All read</button>
                    <span className="text-gray-300">·</span>
                    <button type="button" onClick={() => setAllPerms({ read: true, create: true, edit: true, delete: true })} className="font-semibold text-emerald-600 hover:underline">All full</button>
                    <span className="text-gray-300">·</span>
                    <button type="button" onClick={() => setAllPerms(null)} className="font-semibold text-gray-500 hover:underline">Clear</button>
                  </>
                )}
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              Choose exactly what this employee can do in each module. Tap a capability to toggle it; a card with nothing lit means no access.
              <span className="block mt-0.5"><span className="font-medium text-gray-600">Create</span> without <span className="font-medium text-gray-600">Read</span> = data-entry only (add, no list).</span>
            </p>
            {grantableModules.length === 0 ? (
              <p className="text-xs text-gray-400 px-4 py-6 text-center border border-dashed border-gray-200 rounded-xl">No modules available to grant.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {grantableModules.map((m) => {
                  const perm = form.modulePerms[m.id];
                  const active = anyPerm(perm);
                  const caps: { key: keyof ModulePerm; label: string; onCls: string }[] = [
                    { key: 'read', label: 'Read', onCls: 'bg-blue-600 text-white border-blue-600' },
                    { key: 'create', label: 'Create', onCls: 'bg-blue-600 text-white border-blue-600' },
                    { key: 'edit', label: 'Edit', onCls: 'bg-blue-600 text-white border-blue-600' },
                    { key: 'delete', label: 'Delete', onCls: 'bg-rose-600 text-white border-rose-600' },
                  ];
                  return (
                    <div key={m.id} className={`rounded-xl border p-3 transition-colors ${active ? 'border-blue-200 bg-blue-50/40' : 'border-gray-200 bg-white'}`}>
                      <div className="flex items-center gap-2 mb-2.5">
                        <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${active ? 'bg-blue-500' : 'bg-gray-300'}`} />
                        <span className="truncate text-sm font-semibold text-gray-800" title={m.displayName || m.name}>{m.displayName || m.name}</span>
                      </div>
                      <div className="grid grid-cols-4 gap-1.5">
                        {caps.map(({ key, label, onCls }) => {
                          const on = !!perm?.[key];
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => togglePerm(m.id, key)}
                              aria-pressed={on}
                              className={`px-1 py-1 rounded-md text-[11px] font-semibold border transition-colors ${on ? onCls : 'bg-gray-50 text-gray-400 border-gray-200 hover:bg-gray-100 hover:text-gray-600'}`}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-1">
            <button onClick={onCancel} className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Cancel</button>
            <button onClick={onSubmit} disabled={saving} className="px-5 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg shadow-sm hover:bg-blue-700 disabled:opacity-50 transition-colors">
              {saving ? 'Saving…' : (editingId ? 'Save Changes' : 'Create Employee')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeFormPanel;
